/**
 * Kalyanawaran — Subscription Plan Configuration
 *
 * This is the single source of truth for all plan definitions.
 * To add a new feature: add an entry to PLAN_FEATURES.
 * To change a limit: edit the value inside PLANS[planKey].features[featureKey].
 *
 * Feature value conventions:
 *   true        → fully included (✅)
 *   false       → not included (❌)
 *   number      → numeric limit (e.g. 5 searches/day)
 *   "unlimited" → no limit
 *   string      → descriptive label (e.g. "Email only")
 */

// ─── Feature Metadata ────────────────────────────────────────────────────────
// Defines display label, category, and how the value should be rendered in UI.

export const PLAN_FEATURES = {
  profileCreation: {
    label: "Profile Creation",
    category: "Profile",
    description: "Create and manage your matrimony profile",
    renderType: "boolean", // "boolean" | "limit" | "text"
  },
  horoscopeDetails: {
    label: "Horoscope Details",
    category: "Profile",
    description: "Add Rasi, Nakshatra, and Navamsa Katam details",
    renderType: "boolean",
  },
  searchProfiles: {
    label: "Search Profiles",
    category: "Search",
    description: "Number of profile searches allowed per day",
    renderType: "limit",
    unit: "/day",
  },
};

// ─── Plan Definitions ─────────────────────────────────────────────────────────

export const PLANS = {
  free: {
    key: "free",
    name: "Free",
    tagline: "Get started for free",
    price: 0,
    currency: "INR",
    billingCycle: null, // null = no billing
    badge: null,
    highlightColor: "#6B7280", // gray
    features: {
      profileCreation: true,
      horoscopeDetails: true,
      searchProfiles: 1,
    },
  },

  silver: {
    key: "silver",
    name: "Silver",
    tagline: "Perfect for serious seekers",
    price: 999,
    currency: "INR",
    billingCycle: "12 months",
    badge: null,
    highlightColor: "#9CA3AF", // silver-gray
    features: {
      profileCreation: true,
      horoscopeDetails: true,
      searchProfiles: 5,
    },
  },

  gold: {
    key: "gold",
    name: "Gold",
    tagline: "Most popular choice",
    price: 1999,
    currency: "INR",
    billingCycle: "12 months",
    badge: "Most Popular",
    highlightColor: "#C9A84C", // gold
    features: {
      profileCreation: true,
      horoscopeDetails: true,
      searchProfiles: 5,
    },
  },

  platinum: {
    key: "platinum",
    name: "Platinum",
    tagline: "The complete experience",
    price: 3499,
    currency: "INR",
    billingCycle: "12 months",
    badge: "Best Value",
    highlightColor: "#6366F1", // indigo/platinum
    features: {
      profileCreation: true,
      horoscopeDetails: true,
      searchProfiles: 20,
    },
  },
};

// Ordered list of plan keys for consistent rendering
export const PLAN_ORDER = ["free", "silver", "gold", "platinum"];

// ─── Helper Utilities ─────────────────────────────────────────────────────────

/**
 * Get the display string for a feature value in a given plan.
 * e.g. getFeatureDisplay("searchProfiles", "gold") → "5/day"
 */
export const getFeatureDisplay = (featureKey, planKey) => {
  const feature = PLAN_FEATURES[featureKey];
  const plan = PLANS[planKey];
  if (!feature || !plan) return "—";

  const value = plan.features[featureKey];

  if (feature.renderType === "boolean") {
    return value === true ? "included" : "not-included";
  }
  if (feature.renderType === "limit") {
    if (value === "unlimited") return "unlimited";
    if (typeof value === "number") return `${value}${feature.unit || ""}`;
    return "—";
  }
  if (feature.renderType === "text") {
    return value || "—";
  }
  return String(value);
};

/**
 * Check if a user with the given plan is allowed to use a feature.
 * Returns { allowed: boolean, limit: number|"unlimited"|null }
 */
export const checkFeatureAccess = (featureKey, planKey) => {
  const plan = PLANS[planKey];
  if (!plan) return { allowed: false, limit: null };

  const value = plan.features[featureKey];
  if (value === false || value === undefined) return { allowed: false, limit: null };
  if (value === true) return { allowed: true, limit: null };
  if (value === "unlimited") return { allowed: true, limit: "unlimited" };
  if (typeof value === "number") return { allowed: value > 0, limit: value };
  return { allowed: Boolean(value), limit: null };
};

/**
 * Format price for display
 * e.g. formatPrice(999, "INR") → "₹999"
 */
export const formatPrice = (price, currency = "INR") => {
  if (price === 0) return "Free";
  const symbols = { INR: "₹", USD: "$" };
  return `${symbols[currency] || currency} ${price.toLocaleString("en-IN")}`;
};
